import axios from 'axios';
import i18n from 'i18next';

import { enterMaintenance, isMaintenanceError } from '../utils/maintenanceMode';
import { rateLimitMessage } from '../utils/rateLimit';

/**
 * Pre-configured Axios instance for API communication.
 * Includes base URL, credentials, and timeout settings.
 */
const client = axios.create({
  baseURL: '/api/v1',
  withCredentials: true, // Required for HttpOnly cookie transmission
  timeout: 30000, // 30 second timeout
});

/**
 * Auth epoch: bumped whenever the effective bearer token changes (login, logout,
 * expiry). Each request is stamped with the epoch it was issued under so a late
 * 401 for a request made before the current sign-in cannot clear the new session.
 */
let authEpoch = 0;
let currentToken: string | null = null;

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Auth epoch at the time the request was issued (set by the request interceptor). */
    authEpoch?: number;
  }
}

client.interceptors.request.use((config) => {
  config.authEpoch = authEpoch;
  return config;
});

/**
 * Sets or clears the Authorization header for API requests.
 * @param token - JWT token to set, or null to clear authentication
 */
export const setAuthToken = (token: string | null) => {
  if (token !== currentToken) {
    currentToken = token;
    authEpoch += 1;
  }
  if (token) {
    client.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete client.defaults.headers.common.Authorization;
  }
};

/**
 * Callback function invoked when a 401 authentication error occurs.
 * Set via setAuthErrorHandler to allow AuthContext to handle session expiry.
 */
let onAuthError: (() => void) | null = null;

/**
 * Registers a callback to handle authentication errors (401 responses).
 * Pass null to unregister (e.g., when the AuthProvider unmounts) so the
 * interceptor never invokes a stale handler over dead component state.
 * @param handler - Function to call when authentication fails, or null to clear
 */
export const setAuthErrorHandler = (handler: (() => void) | null) => {
  onAuthError = handler;
};

// Global response error handling
client.interceptors.response.use(
  (response) => response,
  (error) => {
    // Extract API error message and set on error object for consistent error handling
    if (error.response?.data && typeof error.response.data === 'object') {
      const data = error.response.data as { error?: string; message?: string };
      if (data.error) {
        error.message = data.error;
      } else if (data.message) {
        error.message = data.message;
      }
    }

    // Uses the global i18next instance initialised by src/i18n.ts at app startup
    // Replace the generic throttle body with a localized wait message (any 429, app-wide)
    const throttleMessage = rateLimitMessage(i18n.t, error);
    if (throttleMessage) {
      error.message = throttleMessage;
    }

    // The server is optimizing its database (503 {"maintenance": true}): switch to
    // the maintenance view. This is deliberately NOT an auth failure (no logout,
    // no token clearing) and the request is not retried here.
    if (isMaintenanceError(error)) {
      enterMaintenance();
      return Promise.reject(error);
    }

    // Handle 401 authentication errors - triggers auth error callback for session expiry
    if (error.response?.status === 401) {
      console.warn('Authentication failed:', error.config?.url);
      // Skip auth error handling for login/auth endpoints to avoid redirect loops
      const url = error.config?.url || '';
      const isAuthEndpoint =
        url.includes('/auth/login') ||
        url.includes('/auth/me') ||
        url.includes('/auth/logout') ||
        url.includes('/auth/refresh');
      // A 401 for a request issued under an earlier token (e.g. an anonymous call
      // that resolves after a fresh login) says nothing about the current session.
      const isStale = error.config?.authEpoch !== undefined && error.config.authEpoch !== authEpoch;
      if (onAuthError && !isAuthEndpoint && !isStale) {
        onAuthError();
      }
    }
    return Promise.reject(error);
  }
);

export default client;
